<template>
    <vvo v-slot="v" tag="div">
        <section class="sc-section is-collapsible" :class="{ 'is-open': open }">
            <div
                class="sc-section-title"
                role="button"
                tabindex="0"
                :aria-expanded="String(open)"
                @click="open = !open"
                @keydown.enter="open = !open"
            >
                <h2>
                    {{ $t("예외일") }}
                    <span class="sc-meta" style="margin-left: 8px; font-weight: 500">
                        {{ $t("제외") }} {{ activeCount }}{{ $t("일") }} · {{ $t("전체") }}
                        {{ detail.exceptConfig.length }}{{ $t("건") }}
                    </span>
                </h2>
                <span style="color: #1f5fbf; font-size: 12px; font-weight: 700">{{
                    open ? $t("접기 ▴") : $t("펼쳐서 편집 ▾")
                }}</span>
            </div>

            <template v-if="open">
                <div
                    style="
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        gap: 8px;
                        margin-bottom: 12px;
                    "
                >
                    <span class="sc-meta" style="font-size: 12px">{{
                        $t("달력의 날짜를 눌러 추가/편집할 수 있습니다.")
                    }}</span>
                    <div style="display: flex; gap: 8px">
                        <button type="button" class="sc-btn" @click="$refs.CopyModal.show()">
                            {{ $t("예외일 불러오기") }}
                        </button>
                        <button type="button" class="sc-btn sc-btn--outline" @click="openNew(null)">
                            + {{ $t("날짜 추가") }}
                        </button>
                    </div>
                </div>

                <ExceptionCalendar :exceptions="detail.exceptConfig" editable @day-click="onDayClick" />

                <div v-if="sorted.length" class="sc-except-grid">
                    <div v-for="item in sorted" :key="item.exceptDt" class="sc-except-item">
                        <span class="d">{{ formatDate(item.exceptDt) }} ({{ weekdayLabel(item.exceptDt) }})</span>
                        <button
                            type="button"
                            class="n"
                            style="border: none; background: none; text-align: left; padding: 0"
                            @click="openEdit(item)"
                        >
                            {{ item.exceptName }}
                        </button>
                        <button
                            type="button"
                            role="switch"
                            class="sc-switch"
                            :class="{ 'is-on': item.enabled !== 'N' }"
                            :aria-checked="String(item.enabled !== 'N')"
                            :aria-label="`${item.exceptName} ${$t('사용 여부')}`"
                            @click="item.enabled = item.enabled === 'N' ? 'Y' : 'N'"
                        ></button>
                        <button type="button" class="sc-target-remove" :aria-label="$t('삭제')" @click="remove(item)">
                            ✕
                        </button>
                    </div>
                </div>
            </template>

            <b-modal modal-class="sch-modal" ref="DetailModal" centered @hidden="onHidden" @shown="onShown">
                <template #modal-header>
                    <h5>{{ editKey === null ? $t("예외일 추가") : $t("예외일 편집") }}</h5>
                </template>
                <template #modal-footer>
                    <ModalFooter @confirm="onValidate(v, applyException)" @cancel="$refs.DetailModal.hide()" />
                </template>
                <ExceptionDayDetailPanel ref="DetailPanel" :data="detail" :mode="'edit'" />
            </b-modal>

            <b-modal modal-class="sch-modal" ref="CopyModal" @hidden="noop">
                <template #modal-header>
                    <h5>{{ $t("예외일 불러오기") }}</h5>
                </template>
                <template #modal-footer>
                    <ModalFooter @confirm="applyCopy" @cancel="$refs.CopyModal.hide()" />
                </template>
                <ExceptionDayCopyPanel ref="CopyPanel" :data="detail" />
            </b-modal>
        </section>
    </vvo>
</template>

<script>
import ModalFooter from "../ModalFooter.vue";
import ExceptionCalendar from "../ExceptionCalendar.vue";
import ExceptionDayDetailPanel from "../../panels/ExceptionDayDetailPanel.vue";
import ExceptionDayCopyPanel from "../../panels/ExceptionDayCopyPanel.vue";
import { formatDate, weekdayLabel, activeExceptMap } from "../../utils/scheduleFormat";

export default {
    components: { ModalFooter, ExceptionCalendar, ExceptionDayDetailPanel, ExceptionDayCopyPanel },
    props: {
        detail: { type: Object, required: true },
    },
    data() {
        return {
            open: true, // 편집/신규 모두 펼친 상태로 시작
            editKey: null, // 편집 중인 예외일의 원래 exceptDt (신규는 null)
            pending: null,
        };
    },
    computed: {
        sorted() {
            return [...this.detail.exceptConfig].sort((a, b) => new Date(a.exceptDt) - new Date(b.exceptDt));
        },
        activeCount() {
            return Object.keys(activeExceptMap(this.detail.exceptConfig)).length;
        },
    },
    methods: {
        formatDate,
        weekdayLabel,
        noop() {},
        /** 달력 날짜 클릭: 이미 있으면 편집, 없으면 해당 날짜로 추가 */
        onDayClick(date, existing) {
            if (existing) this.openEdit(existing);
            else this.openNew(date);
        },
        openNew(date) {
            this.editKey = null;
            this.pending = date
                ? { exceptName: "", exceptDt: date, enabled: "Y", schdCtrlIdx: this.detail.schdCtrlIdx }
                : null;
            this.$refs.DetailModal.show();
        },
        openEdit(item) {
            this.editKey = item.exceptDt;
            this.pending = { ...item }; // 취소해도 원본이 바뀌지 않도록 복사본으로 편집
            this.$refs.DetailModal.show();
        },
        onShown() {
            this.$refs.DetailPanel.setData(this.pending);
        },
        onHidden() {
            this.editKey = null;
            this.pending = null;
        },
        remove(item) {
            this.detail.exceptConfig = this.detail.exceptConfig.filter((e) => e.exceptDt !== item.exceptDt);
        },
        applyException() {
            const except = this.$refs.DetailPanel.getData();
            if (!except) return;
            const list = this.detail.exceptConfig;
            const dup = list.findIndex((e) => e.exceptDt === except.exceptDt && e.exceptDt !== this.editKey);
            if (dup >= 0) {
                this.alertNoti(this.$t("이미 등록된 예외일자입니다."));
                return;
            }
            const at = this.editKey === null ? -1 : list.findIndex((e) => e.exceptDt === this.editKey);
            if (at >= 0) this.$set(list, at, except);
            else list.push(except);
            list.sort((a, b) => new Date(a.exceptDt) - new Date(b.exceptDt));
            this.$refs.DetailModal.hide();
        },
        applyCopy() {
            const copied = this.$refs.CopyPanel.getData() || [];
            const exists = new Set(this.detail.exceptConfig.map((e) => e.exceptDt));
            copied.filter((e) => !exists.has(e.exceptDt)).forEach((e) => this.detail.exceptConfig.push(e));
            this.detail.exceptConfig.sort((a, b) => new Date(a.exceptDt) - new Date(b.exceptDt));
            this.$refs.CopyModal.hide();
        },
    },
};
</script>
